import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Api from '../../../../component/Api';

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const WORKER_SRC = `
self.onmessage = function(e) {
    const { id, b64 } = e.data;
    try {
        const bin = atob(b64);
        const len = bin.length;
        const buf = new Uint8Array(len);
        for (let i = 0; i < len; i++) buf[i] = bin.charCodeAt(i);
        self.postMessage({ id, buf }, [buf.buffer]);
    } catch(err) {
        self.postMessage({ id, error: err.message });
    }
};
`;

let _worker = null;
const _pending = new Map();
let _workerId = 0;

function getWorker() {
    if (_worker) return _worker;
    try {
        const blob = new Blob([WORKER_SRC], { type: 'text/javascript' });
        _worker = new Worker(URL.createObjectURL(blob));
        _worker.onmessage = ({ data }) => {
            const cb = _pending.get(data.id);
            if (!cb) return;
            _pending.delete(data.id);
            data.error ? cb.reject(new Error(data.error)) : cb.resolve(data.buf);
        };
    } catch {
        _worker = null;
    }
    return _worker;
}

function base64ToBytesAsync(b64) {
    const worker = getWorker();
    if (!worker) {
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return Promise.resolve(bytes);
    }
    const id = _workerId++;
    return new Promise((resolve, reject) => {
        _pending.set(id, { resolve, reject });
        worker.postMessage({ id, b64 });
    });
}

function triggerDownloadFromBytes(bytes, filename) {
    const url = URL.createObjectURL(new Blob([bytes], { type: EXCEL_MIME }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const useGenerate = (file, date) => {
    const { http } = useMemo(() => Api(), []);

    const [isGenerating, setIsGenerating] = useState(false);
    const [timetableUrl, setTimetableUrl] = useState(null);
    const [generateError, setGenerateError] = useState(null);
    const [token, setToken] = useState(null);

    const [isLoadingSC, setIsLoadingSC] = useState(false);
    const [studentCountBytes, setStudentCountBytes] = useState(null);
    const [studentCountFilename, setStudentCountFilename] = useState('StudentCount.xlsx');
    const [scError, setScError] = useState(null);
    const [isBgLoadingSC, setIsBgLoadingSC] = useState(false);

    const [isLoadingCM, setIsLoadingCM] = useState(false);
    const [conflictMatrixBytes, setConflictMatrixBytes] = useState(null);
    const [conflictMatrixFilename, setConflictMatrixFilename] = useState('ConflictMatrix.xlsx');
    const [cmError, setCmError] = useState(null);
    const [isBgLoadingCM, setIsBgLoadingCM] = useState(false);

    const urlsRef = useRef({});
    const scPromiseRef = useRef(null);
    const cmPromiseRef = useRef(null);

    useEffect(() => () => {
        Object.values(urlsRef.current).forEach(u => u && URL.revokeObjectURL(u));
    }, []);

    const prevFileRef = useRef(null);
    const prevDateRef = useRef(null);
    useEffect(() => {
        if (file === prevFileRef.current && date === prevDateRef.current) return;
        prevFileRef.current = file;
        prevDateRef.current = date;

        Object.values(urlsRef.current).forEach(u => u && URL.revokeObjectURL(u));
        urlsRef.current = {};
        scPromiseRef.current = null;
        cmPromiseRef.current = null;

        setTimetableUrl(null);
        setStudentCountBytes(null);
        setConflictMatrixBytes(null);
        setGenerateError(null);
        setScError(null);
        setCmError(null);
        setToken(null);
        setIsBgLoadingSC(false);
        setIsBgLoadingCM(false);
    }, [file, date]);

    const handleGenerate = useCallback(async () => {
        if (!file || !date) return;

        setIsGenerating(true);
        setTimetableUrl(null);
        setStudentCountBytes(null);
        setConflictMatrixBytes(null);
        setGenerateError(null);
        setScError(null);
        setCmError(null);
        setToken(null);
        setIsBgLoadingSC(false);
        setIsBgLoadingCM(false);
        scPromiseRef.current = null;
        cmPromiseRef.current = null;

        Object.values(urlsRef.current).forEach(u => u && URL.revokeObjectURL(u));
        urlsRef.current = {};

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('start_date', date);

            const { data } = await http.post('/admin/timetable/uploadGenerate', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (!data?.timetable?.data || !data?.token) {
                setGenerateError(data?.message ?? 'Generation failed. Invalid response from server.');
                return;
            }

            const bytes = await base64ToBytesAsync(data.timetable.data);
            const url = URL.createObjectURL(new Blob([bytes], { type: EXCEL_MIME }));
            urlsRef.current.timetable = url;
            setTimetableUrl({ url, filename: data.timetable.filename ?? 'Timetable.xlsx' });
            setToken(data.token);

        } catch (err) {
            setGenerateError(err?.response?.data?.message ?? 'An unexpected error occurred. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    }, [file, date, http]);

    const makeSCPromise = useCallback((tok) => {
        if (scPromiseRef.current) return scPromiseRef.current;

        scPromiseRef.current = (async () => {
            const { data } = await http.post('/admin/timetable/downloadStudentCount', { token: tok });
            if (!data?.studentCount?.data) throw new Error(data?.message ?? 'Failed to generate Student Count file.');
            const bytes = await base64ToBytesAsync(data.studentCount.data);
            const filename = data.studentCount.filename ?? 'StudentCount.xlsx';
            setStudentCountBytes(bytes);
            setStudentCountFilename(filename);
            return { bytes, filename };
        })();

        return scPromiseRef.current;
    }, [http]);

    const makeCMPromise = useCallback((tok) => {
        if (cmPromiseRef.current) return cmPromiseRef.current;

        cmPromiseRef.current = (async () => {
            const { data } = await http.post('/admin/timetable/downloadConflictMatrix', { token: tok });
            if (!data?.conflictMatrix?.data) throw new Error(data?.message ?? 'Failed to generate Conflict Matrix file.');
            const bytes = await base64ToBytesAsync(data.conflictMatrix.data);
            const filename = data.conflictMatrix.filename ?? 'ConflictMatrix.xlsx';
            setConflictMatrixBytes(bytes);
            setConflictMatrixFilename(filename);
            return { bytes, filename };
        })();

        return cmPromiseRef.current;
    }, [http]);

    useEffect(() => {
        if (!token) return;

        setIsBgLoadingSC(true);
        makeSCPromise(token)
            .catch(() => { scPromiseRef.current = null; })
            .finally(() => setIsBgLoadingSC(false));

        setIsBgLoadingCM(true);
        makeCMPromise(token)
            .catch(() => { cmPromiseRef.current = null; })
            .finally(() => setIsBgLoadingCM(false));
    }, [token, makeSCPromise, makeCMPromise]);

    const handleDownloadStudentCount = useCallback(async () => {
        if (!token) return;

        if (studentCountBytes) {
            triggerDownloadFromBytes(studentCountBytes, studentCountFilename);
            return;
        }

        setIsLoadingSC(true);
        setScError(null);

        try {
            const { bytes, filename } = await makeSCPromise(token);
            triggerDownloadFromBytes(bytes, filename);
        } catch (err) {
            scPromiseRef.current = null;
            setScError(err?.message ?? 'Session expired or error. Please generate again.');
        } finally {
            setIsLoadingSC(false);
        }
    }, [token, studentCountBytes, studentCountFilename, makeSCPromise]);

    const handleDownloadConflictMatrix = useCallback(async () => {
        if (!token) return;

        if (conflictMatrixBytes) {
            triggerDownloadFromBytes(conflictMatrixBytes, conflictMatrixFilename);
            return;
        }

        setIsLoadingCM(true);
        setCmError(null);

        try {
            const { bytes, filename } = await makeCMPromise(token);
            triggerDownloadFromBytes(bytes, filename);
        } catch (err) {
            cmPromiseRef.current = null;
            setCmError(err?.message ?? 'Session expired or error. Please generate again.');
        } finally {
            setIsLoadingCM(false);
        }
    }, [token, conflictMatrixBytes, conflictMatrixFilename, makeCMPromise]);

    return {
        isGenerating, generateError, handleGenerate,
        timetableUrl,
        token,
        isLoadingSC: isLoadingSC || isBgLoadingSC,
        studentCountUrl: studentCountBytes ? { filename: studentCountFilename } : null,
        scError, handleDownloadStudentCount,
        isLoadingCM: isLoadingCM || isBgLoadingCM,
        conflictMatrixUrl: conflictMatrixBytes ? { filename: conflictMatrixFilename } : null,
        cmError, handleDownloadConflictMatrix,
    };
};